<template>
  <div
    v-if="Object.values(cards).length > 1"
    class="container-lead tw:mb-4 tw:grid tw:grid-cols-[repeat(auto-fit,minmax(275px,1fr))] tw:gap-4">
    <div
      v-for="card in Object.values(cards).filter((c) => !!c)"
      :key="card.key"
      class="tw:flex tw:min-h-40 tw:flex-col tw:justify-between tw:rounded-md tw:border tw:border-solid tw:border-border tw:bg-surface tw:shadow-card tw:px-6 tw:py-4">
      <component v-if="cardComponentMap[card.type]" :is="cardComponentMap[card.type]" :data="card.data">
        <template #popover-box>
          <Popover align="right" width="250px" :skidding="12" :z-index="20" panel-class="tw:p-4">
            <template #trigger="{ toggleDropdown }">
              <LeadButton
                variant="ghost"
                size="sm"
                :icon="IconDotsVertical"
                :aria-label="$t('customer.cards.settings.title')"
                @click="toggleDropdown" />
            </template>
            <template #dropdown="{ closeDropdown }">
              <div class="tw:mb-4 tw:flex tw:items-center tw:justify-between">
                <div class="tw:text-15 tw:font-medium tw:text-gray-700">
                  {{ $t('customer.cards.settings.title') }}
                </div>
                <LeadButton
                  variant="ghost"
                  size="sm"
                  :icon="IconX"
                  :aria-label="$t('common.close')"
                  @click="closeDropdown" />
              </div>
              <LeadSelect
                :model-value="card.key"
                :options="metricOptions(card.key)"
                :label="$t('customer.cards.settings.metric_label')"
                :placeholder="$t('customer.cards.settings.metric_placeholder')"
                :filterable="false"
                @update:model-value="(next) => onMetricChange(card.key, next as string)" />
            </template>
          </Popover>
        </template>
      </component>
      <div v-else>Loading card or unknown type: {{ card.type }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { fetchMetrics, fetchStats, saveMetrics } from '@/api/customer';
import { defineAsyncComponent, onMounted, ref, shallowRef } from 'vue';
import { useToast } from 'vue-toastification';
import { IconDotsVertical, IconX } from '@tabler/icons-vue';
import Popover from '@/components/common/Popover.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadSelect, { type LeadSelectOption } from '@/components/lead-design/LeadSelect.vue';

// --- Dynamic Component Imports ---
// Use shallowRef for components to prevent deep reactivity overhead
// Use defineAsyncComponent for lazy loading (optional but good practice)
const cardComponentMap = shallowRef({
  text: defineAsyncComponent(() => import('./cards/TextCard.vue')),
  line_chart: defineAsyncComponent(() => import('./cards/LineChartCard.vue')),
  heatmap: defineAsyncComponent(() => import('./cards/HeatmapCard.vue')),
});

interface CardFromBackend {
  key: string;
  type: 'text' | 'line_chart' | 'heatmap';
  data: any;
}

const cards = ref<Record<string, CardFromBackend>>({});
const allMetrics = ref();
const isLoading = ref(true);
const locale = ref();
const toast = useToast();

// Options for the metric picker in a given card: every available metric except the ones already
// picked by OTHER cards (so the same metric can't appear twice). The current card's own metric stays
// in the list — that's the pre-selected value.
const metricOptions = (currentCardKey: string): LeadSelectOption[] => {
  const takenByOthers = new Set(
    Object.values(cards.value)
      .filter((card): card is CardFromBackend => !!card)
      .map((card) => card.key)
      .filter((key) => key !== currentCardKey)
  );

  return Object.entries(allMetrics.value ?? {})
    .filter(([key]) => !takenByOthers.has(key))
    .map(([value, label]) => ({ value, label: label as string }));
};

onMounted(async () => {
  // get browser locale or fallback to de_CH
  const browserLocale = navigator.language;
  locale.value = browserLocale ? browserLocale.replace('-', '_') : 'de_CH';
  isLoading.value = true;
  try {
    // Get available metrics from the backend
    const metricsResponse = await fetchMetrics();
    allMetrics.value = metricsResponse.stats ?? {};
    await fetch();
  } catch (err: any) {
    console.error('Error fetching initial card data or metrics:', err);
    cards.value = {};
  } finally {
    isLoading.value = false;
  }
});

const fetch = async () => {
  const response = await fetchStats({ locale: locale.value });

  if (response.status === 'success') {
    const res = response.stats;
    const tempCards: Record<string, CardFromBackend> = {};
    Object.keys(res).forEach((key) => {
      if (res[key] === null && allMetrics.value[key]) {
        // If the backend returns null for a card, we set it to null in our state
        tempCards[key] = { key: key, type: 'text', data: { title: allMetrics.value[key], main_title: 'N/A' } };
      } else tempCards[key] = res[key];
    });
    cards.value = tempCards;
  } else {
    console.warn('Invalid card data structure received:', response);
    cards.value = {};
  }
};

const onMetricChange = async (previousKey: string, nextKey: string) => {
  if (!nextKey || nextKey === previousKey) return;
  // Swap the picked card's key in the current cards map, then persist the full ordered list.
  // Previously PopoverBox mutated `card.key` via v-model directly; the model is now one-way, so we
  // rebuild `cards` here (order-preserving) before saving.
  const rebuilt: Record<string, CardFromBackend> = {};
  for (const [slot, card] of Object.entries(cards.value)) {
    if (!card) continue;
    rebuilt[slot] = card.key === previousKey ? { ...card, key: nextKey } : card;
  }
  cards.value = rebuilt;

  try {
    const data = Object.values(cards.value)
      .filter((c) => !!c)
      .map((card) => (card as CardFromBackend).key);
    await saveMetrics({ stats: data });
    await fetch();
  } catch (err: any) {
    console.error('Error saving metrics:', err);
    toast.error('Error saving metrics');
  }
};
</script>

<style scoped>
/* Typography for card children (TextCard, HeatmapCard, LineChartCard). Kept as :deep() rather than
   moved into each child so this migration stays scoped to CustomerCards; the child components can
   later inline these with tw:text-16/tw:text-30 and delete these rules. */
:deep(.title) {
  font-size: 13px;
  font-weight: 400;
  color: rgb(102, 112, 133);
  height: 20px;
  line-height: 20px;
}

:deep(.main-title) {
  font-size: 30px;
  font-weight: 700;
  color: rgb(16, 24, 40);
  margin-top: 6px;
  line-height: 30px;
  height: 30px;
}
</style>
